import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db, functions } from '../firebase';
import { collection, getDocs, updateDoc, doc, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import DocumentPreview from '../components/DocumentPreview';
import PlagiarismDetectionService from '../services/plagiarismDetection';
// Jotform integration removed — external form submissions now use Google Forms
import '../styles/brand.css';
import { UI_TIMEOUTS } from '../constants/ui';

const FacilitatorGradebook = () => {
  usePageTitle("Gradebook");
  const { currentUser, userProfile } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [learners, setLearners] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGradingModal, setShowGradingModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [gradingData, setGradingData] = useState({
    score: '',
    feedback: '',
    rubricScores: {}
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [submissionsPage, setSubmissionsPage] = useState(1);
  const [overviewPage, setOverviewPage] = useState(1);
  const PAGE_SIZE = 25;

  const isActiveLearner = (learner) => {
    if (!learner) return false;
    if (learner.deleted || learner.deactivated) return false;
    if (learner.isActive === false) return false;
    const status = String(learner.status || '').toLowerCase();
    if (['inactive', 'dropoff', 'drop_off', 'drop off', 'dropped'].includes(status)) return false;
    return true;
  };

  const sanitizeFilePart = (value) =>
    String(value || '')
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 80);

  const sanitizeLabelPart = (value) =>
    String(value || '')
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '')
      .replace(/\s+/g, ' ')
      .slice(0, 80);

  const getLearnerCohortIds = (learner) => ([
    learner?.cohortId,
    ...(Array.isArray(learner?.cohortIds) ? learner.cohortIds : []),
    ...(Array.isArray(learner?.assignedCohorts) ? learner.assignedCohorts : [])
  ].filter(Boolean));

  const isWordDocument = (fileName = '') => /\.(doc|docx)$/i.test(String(fileName || ''));

  const buildSingleFileDownloadName = (file, submission) => {
    const learnerName = sanitizeLabelPart(submission?.learnerName || 'Learner');
    const idNumber = sanitizeLabelPart(submission?.learnerIdNumber || 'No ID');
    const assignmentName = sanitizeLabelPart(submission?.assignmentTitle || 'Assignment');
    const rawName = sanitizeLabelPart(file?.name || file?.originalName || 'Submission File');
    return `${learnerName} - ${idNumber} - ${assignmentName} - ${rawName}`;
  };

  const openFilePreview = (file, submission = null) => {
    if (!file?.url) return;
    setPreviewFile({
      ...file,
      downloadName: buildSingleFileDownloadName(file, submission)
    });
    setShowPreviewModal(true);
  };

  const downloadAllSubmissionFiles = async () => {
    const files = [];
    filteredSubmissions.forEach((submission) => {
      const learnerName = sanitizeLabelPart(submission.learnerName || 'Learner');
      const idNumber = sanitizeLabelPart(submission.learnerIdNumber || 'No ID');
      const assignmentName = sanitizeLabelPart(submission.assignmentTitle || 'Assignment');
      (submission.files || []).forEach((file, idx) => {
        const rawName = sanitizeLabelPart(file.name || file.originalName || `File ${idx + 1}`);
        files.push({
          url: file.url,
          fileName: `${learnerName} - ${idNumber} - ${assignmentName} - ${rawName}`,
          learnerName,
          assignmentName
        });
      });
    });

    if (files.length === 0) {
      showAlert('No submitted files found for the current filters.', 'warning');
      return;
    }

    try {
      setBulkDownloadLoading(true);
      const createAssignmentSubmissionsZip = httpsCallable(functions, 'createAssignmentSubmissionsZip');
      const label = [
        selectedModule !== 'all' ? sanitizeFilePart(modules.find((module) => module.id === selectedModule)?.title || selectedModule) : 'all-modules',
        selectedAssignmentId !== 'all' ? sanitizeFilePart(selectedAssignmentRecord?.title || selectedAssignmentRecord?.assignmentTitle || selectedAssignmentId) : 'all-assignments',
        filterStatus !== 'all' ? sanitizeFilePart(filterStatus) : 'all-statuses'
      ].join('_');
      const result = await createAssignmentSubmissionsZip({ files, label });
      const payload = result?.data || {};

      if (!payload.downloadUrl) {
        throw new Error('Zip download URL was not returned.');
      }

      const link = document.createElement('a');
      link.href = payload.downloadUrl;
      link.download = `${label || 'assignment-submissions'}.zip`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const archivedCount = payload.archivedFileCount ?? files.length;
      const failedCount = Array.isArray(payload.failedFiles) ? payload.failedFiles.length : 0;
      if (failedCount > 0) {
        showAlert(`Downloaded zip for ${archivedCount} file(s). ${failedCount} file(s) could not be included.`, 'warning');
      } else {
        showAlert(`Downloaded zip for ${archivedCount} submission file(s).`, 'success');
      }
    } catch (error) {
      devError('Error creating assignment submissions zip:', error);
      showAlert(`Failed to bulk download submission files: ${error.message}`, 'danger');
    } finally {
      setBulkDownloadLoading(false);
    }
  };
  const toDisplayDate = (value) => {
    if (!value) return '';
    const date = value?.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
  };

  const csvEscape = (value) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const downloadCsv = (rows, filename) => {
    const csvContent = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('all');
  const [selectedLearnerId, setSelectedLearnerId] = useState('all');
  const [selectedCohortId, setSelectedCohortId] = useState('all');
  const [plagiarismResult, setPlagiarismResult] = useState(null);
  const [plagiarismLoading, setPlagiarismLoading] = useState(false);
  const isFacilitator = userProfile?.role === 'facilitator';
  const isAdmin = userProfile?.role === 'admin';
  const canAccessGradebook = isFacilitator || isAdmin;

  useEffect(() => {
    if (currentUser && canAccessGradebook) {
      loadData();
    }
  }, [currentUser, canAccessGradebook]);

  const chunkArray = (items, size = 30) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const toTimestampSafe = (value) => {
        if (!value) return null;
        if (value?.toDate) return value;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
      };

      let modulesData = [];
      if (isAdmin) {
        const modulesSnapshot = await getDocs(collection(db, 'modules'));
        modulesData = modulesSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      } else {
        const modulesSnapshot = await getDocs(
          query(collection(db, 'modules'), where('facilitatorId', '==', currentUser.uid))
        );
        modulesData = modulesSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      }

      const moduleMap = new Map(modulesData.map((m) => [m.id, m]));
      const allAssignments = [];

      const moduleIds = modulesData.map((module) => module.id).filter(Boolean);
      let assignmentsData = [];
      if (isAdmin) {
        const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
        assignmentsData = assignmentsSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      } else if (moduleIds.length > 0) {
        const assignmentChunks = chunkArray(moduleIds, 30);
        const assignmentSnapshots = await Promise.all(
          assignmentChunks.map((chunk) =>
            getDocs(query(collection(db, 'assignments'), where('moduleId', 'in', chunk)))
          )
        );
        const assignmentMap = new Map();
        assignmentSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((assignmentDoc) => {
            assignmentMap.set(assignmentDoc.id, { id: assignmentDoc.id, ...assignmentDoc.data() });
          });
        });
        assignmentsData = Array.from(assignmentMap.values());
      }
      assignmentsData.forEach((assignment) => {
        allAssignments.push({ ...assignment, sourceType: assignment.sourceType || 'assignment' });
      });

      const learnersSnapshot = await getDocs(query(collection(db, 'users'), where('role', '==', 'learner')));
      const learnersData = learnersSnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, uid: docSnap.id, ...docSnap.data() }))
        .filter(isRealUser);

      const facilitatorCohorts = userProfile?.managedCohorts || userProfile?.assignedCohorts || [];
      const filteredLearners = (isAdmin ? learnersData : learnersData.filter((learner) => {
        const learnerCohorts = getLearnerCohortIds(learner);
        return learnerCohorts.some((cohortId) => facilitatorCohorts.includes(cohortId));
      }))
        .filter(isActiveLearner);
      setLearners(filteredLearners);

      const learnerMap = new Map(filteredLearners.map((learner) => [learner.uid, learner]));
      const submissionsData = [];

      for (const assignment of assignmentsData) {
        if (!assignment.submissions) continue;
        Object.entries(assignment.submissions).forEach(([learnerId, submission]) => {
          const learner = learnerMap.get(learnerId);
          if (!learner) return;
          submissionsData.push({
            id: `${assignment.id}_${learnerId}`,
            sourceType: 'assignment',
            canGrade: true,
            assignmentId: assignment.id,
            assignmentTitle: assignment.title,
            moduleId: assignment.moduleId,
            moduleName: moduleMap.get(assignment.moduleId)?.title || 'Assignments',
            learnerId,
            learnerName: learner.displayName || learner.email,
            learnerEmail: learner.email,
            learnerIdNumber: learner.idNumber || '',
            ...submission,
            submittedAt: toTimestampSafe(submission.submittedAt),
            rubric: assignment.rubric,
            maxScore: assignment.maxScore || 100
          });
        });
      }

      // Submission Requests (Resource Hub) as pseudo-assignments
      try {
        let submissionRequests = [];
        if (isAdmin) {
          const reqSnap = await getDocs(collection(db, 'submissionRequests'));
          submissionRequests = reqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } else {
          const reqMap = new Map();
          const reqFetches = [getDocs(query(collection(db, 'submissionRequests'), where('createdById', '==', currentUser.uid)))];
          for (let i = 0; i < facilitatorCohorts.length; i += 10) {
            const chunk = facilitatorCohorts.slice(i, i + 10);
            if (chunk.length) {
              reqFetches.push(
                getDocs(query(collection(db, 'submissionRequests'), where('cohortIds', 'array-contains-any', chunk)))
              );
            }
          }
          const reqSnaps = await Promise.all(reqFetches);
          reqSnaps.forEach((snap) => snap.docs.forEach((d) => reqMap.set(d.id, { id: d.id, ...d.data() })));
          submissionRequests = Array.from(reqMap.values());
        }

        if (submissionRequests.length > 0) {
          moduleMap.set('submission_requests', { id: 'submission_requests', title: 'Submission Requests', synthetic: true });
        }

        for (const req of submissionRequests) {
          const reqCohortIds = Array.isArray(req.cohortIds) ? req.cohortIds : (req.cohortId ? [req.cohortId] : []);
          allAssignments.push({
            id: `submission_request_${req.id}`,
            sourceType: 'submission_request',
            sourceId: req.id,
            title: req.title || 'Submission Request',
            moduleId: 'submission_requests',
            cohortIds: reqCohortIds,
            cohortId: req.cohortId || null,
            programId: req.programId || null,
            createdAt: req.createdAt || null
          });

          let reqSubDocs = [];
          try {
            const reqSubSnap = await getDocs(query(collection(db, 'submissionRequests', req.id, 'submissions'), orderBy('submittedAt', 'desc')));
            reqSubDocs = reqSubSnap.docs;
          } catch (e) {
            const reqSubSnap = await getDocs(collection(db, 'submissionRequests', req.id, 'submissions'));
            reqSubDocs = reqSubSnap.docs;
          }

          reqSubDocs.forEach((docSnap) => {
            const data = docSnap.data();
            const learnerId = data.learnerId || docSnap.id;
            const learner = learnerMap.get(learnerId);
            if (!learner) return;
            if (reqCohortIds.length > 0 && !reqCohortIds.includes(learner.cohortId)) return;
            const files = Array.isArray(data.files)
              ? data.files.map((f, idx) => ({
                  name: f.fileName || `File ${idx + 1}`,
                  originalName: f.fileName || `File ${idx + 1}`,
                  url: f.fileUrl || '',
                  size: f.fileSize || 0,
                  type: f.fileType || ''
                }))
              : (data.fileUrl ? [{
                  name: data.fileName || 'Submission',
                  originalName: data.fileName || 'Submission',
                  url: data.fileUrl,
                  size: data.fileSize || 0,
                  type: data.fileType || ''
                }] : []);
            submissionsData.push({
              id: `submission_request_${req.id}_${learnerId}`,
              sourceType: 'submission_request',
              canGrade: false,
              assignmentId: `submission_request_${req.id}`,
              assignmentTitle: req.title || 'Submission Request',
              moduleId: 'submission_requests',
              moduleName: 'Submission Requests',
              learnerId,
              learnerName: learner.displayName || learner.email,
              learnerEmail: learner.email || data.learnerEmail || '',
              learnerIdNumber: learner.idNumber || '',
              status: data.status === 'approved' ? 'graded' : 'submitted',
              sourceStatus: data.status || 'submitted',
              score: data.score ?? null,
              feedback: data.latestComment || data.comment || '',
              submittedAt: toTimestampSafe(data.submittedAt),
              files,
              maxScore: 100
            });
          });
        }
      } catch (error) {
        devLog('Gradebook: failed to load submission requests', error);
      }

      // Fillable templates as pseudo-assignments
      try {
        const templateSnap = await getDocs(query(collection(db, 'resources'), where('isFillableTemplate', '==', true)));
        const templates = templateSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const scopedTemplates = templates.filter((template) => {
          const templateCohorts = Array.isArray(template.cohortIds) ? template.cohortIds : (template.cohortId ? [template.cohortId] : []);
          if (!isAdmin && templateCohorts.length > 0 && !templateCohorts.some((id) => facilitatorCohorts.includes(id))) {
            return false;
          }
          return true;
        });

        if (scopedTemplates.length > 0) {
          moduleMap.set('fillable_templates', { id: 'fillable_templates', title: 'Fillable Templates', synthetic: true });
        }

        const templateMap = new Map();
        scopedTemplates.forEach((template) => {
          templateMap.set(template.id, template);
          allAssignments.push({
            id: `fillable_template_${template.id}`,
            sourceType: 'fillable_template',
            sourceId: template.id,
            title: template.title || template.name || 'Fillable Template',
            moduleId: 'fillable_templates',
            cohortIds: Array.isArray(template.cohortIds) ? template.cohortIds : (template.cohortId ? [template.cohortId] : []),
            cohortId: template.cohortId || null,
            programId: template.programId || null
          });
        });

        if (templateMap.size > 0) {
          const templateSubSnap = await getDocs(collection(db, 'templateSubmissions'));
          templateSubSnap.docs.forEach((docSnap) => {
            const data = docSnap.data();
            const template = templateMap.get(data.templateId);
            if (!template) return;
            const learner = learnerMap.get(data.learnerId);
            if (!learner) return;
            submissionsData.push({
              id: `fillable_template_${data.templateId}_${docSnap.id}`,
              sourceType: 'fillable_template',
              canGrade: false,
              assignmentId: `fillable_template_${data.templateId}`,
              assignmentTitle: data.templateTitle || template.title || 'Fillable Template',
              moduleId: 'fillable_templates',
              moduleName: 'Fillable Templates',
              learnerId: data.learnerId,
              learnerName: learner.displayName || learner.email,
              learnerEmail: learner.email || data.learnerEmail || '',
              learnerIdNumber: learner.idNumber || '',
              status: data.status === 'approved' ? 'graded' : 'submitted',
              sourceStatus: data.status || 'pending',
              score: data.grade ?? null,
              feedback: data.reviewComments || data.comments || '',
              submittedAt: toTimestampSafe(data.submittedAt),
              files: data.fileURL ? [{
                name: data.fileName || 'Template Submission',
                originalName: data.fileName || 'Template Submission',
                url: data.fileURL,
                size: data.fileSize || 0,
                type: data.fileType || ''
              }] : [],
              maxScore: 100
            });
          });
        }
      } catch (error) {
        devLog('Gradebook: failed to load fillable templates', error);
      }

      setModules(Array.from(moduleMap.values()));
      setAssignments(Array.from(new Map(allAssignments.map((a) => [a.id, a])).values()));
      setSubmissions(submissionsData);
      
    } catch (error) {
      devError('Error loading gradebook data:', error);
      showAlert('Error loading gradebook data: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const selectedAssignmentRecord = assignments.find((a) => a.id === selectedAssignmentId);
  const isSelectedLmsAssignment = !!selectedAssignmentRecord && (!selectedAssignmentRecord.sourceType || selectedAssignmentRecord.sourceType === 'assignment');

  const handleGradeSubmission = async (submission) => {
    setSelectedSubmission(submission);
    setSelectedAssignment(assignments.find(a => a.id === submission.assignmentId));

    // Initialize grading data
    const rubricScores = {};
    if (submission.rubric && submission.rubric.criteria) {
      submission.rubric.criteria.forEach(criterion => {
        rubricScores[criterion.name] = '';
      });
    }

    setGradingData({
      score: submission.score || '',
      feedback: submission.feedback || '',
      rubricScores: rubricScores
    });
    setShowGradingModal(true);

    // Run plagiarism check if submission has text content
    if (submission.content || submission.answer) {
      setPlagiarismLoading(true);
      setPlagiarismResult(null);

      try {
        const plagiarismService = new PlagiarismDetectionService();
        const submissionText = submission.content || submission.answer || '';

        const result = await plagiarismService.checkPlagiarism(
          submissionText,
          submission.assignmentId,
          submission.learnerId
        );

        setPlagiarismResult(result);
      } catch (error) {
        devError('Plagiarism check error:', error);
        // Don't fail the grading process if plagiarism check fails
      } finally {
        setPlagiarismLoading(false);
      }
    }
  };

  const calculateRubricTotal = () => {
    if (!selectedAssignment?.rubric?.criteria) return 0;
    
    return Object.values(gradingData.rubricScores).reduce((total, score) => {
      return total + (parseFloat(score) || 0);
    }, 0);
  };

  const handleSaveGrade = async () => {
    try {
      if (!selectedSubmission || !selectedAssignment) return;

      const finalScore = selectedAssignment.rubric 
        ? calculateRubricTotal() 
        : parseFloat(gradingData.score) || 0;

      // Update the submission in the assignment document
      const assignmentRef = doc(db, 'assignments', selectedSubmission.assignmentId);
      const updatedSubmissions = {
        ...assignments.find(a => a.id === selectedSubmission.assignmentId).submissions,
        [selectedSubmission.learnerId]: {
          ...assignments.find(a => a.id === selectedSubmission.assignmentId).submissions[selectedSubmission.learnerId],
          score: finalScore,
          feedback: gradingData.feedback,
          rubricScores: gradingData.rubricScores,
          status: 'graded',
          gradedAt: serverTimestamp(),
          gradedBy: currentUser.uid,
          plagiarismCheck: plagiarismResult || null
        }
      };

      await updateDoc(assignmentRef, {
        [`submissions.${selectedSubmission.learnerId}`]: updatedSubmissions[selectedSubmission.learnerId]
      });

      showAlert('Grade saved successfully!', 'success');
      setShowGradingModal(false);
      loadData(); // Reload data to show updated grades

    } catch (error) {
      devError('Error saving grade:', error);
      showAlert('Error saving grade: ' + error.message, 'danger');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'graded':
        return <Badge bg="success">Graded</Badge>;
      case 'submitted':
        return <Badge bg="warning">Pending Review</Badge>;
      case 'draft':
        return <Badge bg="secondary">Draft</Badge>;
      case 'late':
        return <Badge bg="danger">Late Submission</Badge>;
      default:
        return <Badge bg="light" text="dark">Not Submitted</Badge>;
    }
  };

  const filteredSubmissions = submissions.filter(submission => {
    if (filterStatus !== 'all' && submission.status !== filterStatus) return false;
    if (selectedModule !== 'all' && submission.moduleId !== selectedModule) return false;
    if (selectedAssignmentId !== 'all' && submission.assignmentId !== selectedAssignmentId) return false;
    if (selectedLearnerId !== 'all' && submission.learnerId !== selectedLearnerId) return false;
    if (selectedCohortId !== 'all') {
      const learner = learners.find((item) => item.id === submission.learnerId || item.uid === submission.learnerId);
      const learnerCohorts = getLearnerCohortIds(learner);
      if (!learnerCohorts.includes(selectedCohortId)) return false;
    }
    return true;
  });

  const isLearnerEligibleForAssignment = (learner, assignment) => {
    if (!learner || !assignment) return false;
    const learnerCohorts = getLearnerCohortIds(learner);
    const assignmentCohorts = Array.isArray(assignment.cohortIds)
      ? assignment.cohortIds.filter(Boolean)
      : (assignment.cohortId ? [assignment.cohortId] : []);

    if (assignment.programId && learner.programId && assignment.programId !== learner.programId) {
      return false;
    }
    if (assignmentCohorts.length > 0) {
      return assignmentCohorts.some((cohortId) => learnerCohorts.includes(cohortId));
    }
    if (selectedModule !== 'all' && assignment.moduleId !== selectedModule) {
      return false;
    }
    return true;
  };

  const statusFromSource = (submission) => {
    const sourceStatus = String(submission?.sourceStatus || submission?.status || '').toLowerCase();
    if (['approved', 'graded', 'verified'].includes(sourceStatus)) return 'Approved';
    if (sourceStatus === 'rejected') return 'Rejected';
    if (sourceStatus === 'revision_requested') return 'Revision Requested';
    if (sourceStatus === 'late') return 'Late';
    if (sourceStatus === 'draft') return 'Draft';
    if (sourceStatus === 'submitted') return 'Submitted';
    return 'Missing';
  };

  const assignmentOverviewRows = assignments
    .filter((assignment) => selectedModule === 'all' || assignment.moduleId === selectedModule)
    .filter((assignment) => selectedAssignmentId === 'all' || assignment.id === selectedAssignmentId)
    .flatMap((assignment) =>
      learners
        .filter((learner) => isLearnerEligibleForAssignment(learner, assignment))
        .map((learner) => {
          const submission = submissions.find(
            (item) => item.assignmentId === assignment.id && item.learnerId === learner.id
          );
          const status = submission ? statusFromSource(submission) : 'Missing';
          return {
            learnerId: learner.id,
            learnerCohortIds: getLearnerCohortIds(learner),
            learnerName: learner.displayName || learner.email,
            learnerEmail: learner.email || '',
            learnerPhone: learner.phone || learner.phoneNumber || '',
            learnerIdNumber: learner.idNumber || '',
            moduleName: modules.find((module) => module.id === assignment.moduleId)?.title || assignment.moduleId || 'Assignments',
            assignmentTitle: assignment.title,
            sourceType: assignment.sourceType || 'assignment',
            status,
            submittedAt: submission?.submittedAt || null,
            feedback: submission?.feedback || '',
            revisionHistory: Array.isArray(submission?.reviewHistory)
              ? submission.reviewHistory.map((entry) => `${entry.status || ''}${entry.comment ? `: ${entry.comment}` : ''}`).join(' | ')
              : '',
            outstanding: status === 'Missing' ? 'Yes' : 'No'
          };
        })
    )
    .filter((row) => {
      if (selectedLearnerId !== 'all' && row.learnerId !== selectedLearnerId) return false;
      if (selectedCohortId !== 'all' && !row.learnerCohortIds.includes(selectedCohortId)) return false;
      if (filterStatus === 'all') return true;
      if (filterStatus === 'submitted') return row.status === 'Submitted';
      if (filterStatus === 'graded') return row.status === 'Approved';
      if (filterStatus === 'late') return row.status === 'Late';
      if (filterStatus === 'draft') return row.status === 'Draft';
      return true;
    });

  const exportFilteredSubmissionsCsv = () => {
    const rows = [
      ['Student Name', 'Student Email', 'Student ID Number', 'Module', 'Assignment', 'Source Type', 'Submission Date', 'Status', 'Score', 'Feedback', 'Revision History']
    ];
    filteredSubmissions.forEach((submission) => {
      rows.push([
        submission.learnerName || '',
        submission.learnerEmail || '',
        submission.learnerIdNumber || '',
        submission.moduleName || '',
        submission.assignmentTitle || '',
        submission.sourceType || 'assignment',
        toDisplayDate(submission.submittedAt),
        statusFromSource(submission),
        submission.score ?? '',
        submission.feedback || '',
        Array.isArray(submission.reviewHistory)
          ? submission.reviewHistory.map((entry) => `${entry.status || ''}${entry.comment ? `: ${entry.comment}` : ''}`).join(' | ')
          : ''
      ]);
    });

    downloadCsv(rows, `gradebook_submissions_${new Date().toISOString().split('T')[0]}.csv`);
    showAlert('Submission report CSV downloaded.', 'success');
  };

  const exportAssignmentOverviewCsv = () => {
    const rows = [
      ['Student Name', 'Student Email', 'Phone', 'ID Number', 'Module', 'Assignment', 'Source Type', 'Status', 'Date Submitted', 'Outstanding', 'Feedback', 'Revision History']
    ];
    assignmentOverviewRows.forEach((row) => {
      rows.push([
        row.learnerName,
        row.learnerEmail,
        row.learnerPhone,
        row.learnerIdNumber,
        row.moduleName,
        row.assignmentTitle,
        row.sourceType,
        row.status,
        toDisplayDate(row.submittedAt),
        row.outstanding,
        row.feedback,
        row.revisionHistory
      ]);
    });

    downloadCsv(rows, `assignment_overview_${new Date().toISOString().split('T')[0]}.csv`);
    showAlert('Assignment overview CSV downloaded.', 'success');
  };

  const pendingGrades = submissions.filter(s => s.status === 'submitted').length;
  const totalSubmissions = submissions.length;
  const gradedSubmissions = submissions.filter(s => s.status === 'graded').length;
  const averageScore = gradedSubmissions > 0 
    ? submissions.filter(s => s.score !== null && s.score !== undefined)
        .reduce((sum, s) => sum + s.score, 0) / gradedSubmissions 
    : 0;
  const cohortOptions = Array.from(
    new Set(
      learners.flatMap((learner) => getLearnerCohortIds(learner))
    )
  ).filter(Boolean);

  if (!canAccessGradebook) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access the gradebook.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <FacilitatorNavbar currentPage="gradebook" />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Gradebook</h1>
              <p className="mission-text mb-4">
                Manage grades and provide feedback to empower learner success
              </p>
              <p className="mb-0">
                Review submissions, assign grades using rubrics, and provide meaningful feedback to support learner development.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {alert.show && (
          <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading gradebook data...</p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-warning">{pendingGrades}</h3>
                    <p className="mb-0">Pending Grades</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-success">{gradedSubmissions}</h3>
                    <p className="mb-0">Graded</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-primary">{totalSubmissions}</h3>
                    <p className="mb-0">Total Submissions</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-info">{averageScore.toFixed(1)}%</h3>
                    <p className="mb-0">Average Score</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Filters */}
            <Row className="mb-4">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Filter by Status</Form.Label>
                  <Form.Control
                    as="select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="submitted">Pending Review</option>
                    <option value="graded">Graded</option>
                    <option value="late">Late Submissions</option>
                    <option value="draft">Drafts</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Filter by Module</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedModule}
                    onChange={(e) => setSelectedModule(e.target.value)}
                  >
                    <option value="all">All Modules</option>
                    {modules.map(module => (
                      <option key={module.id} value={module.id}>
                        {module.title}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Filter by Assignment</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedAssignmentId}
                    onChange={(e) => setSelectedAssignmentId(e.target.value)}
                  >
                    <option value="all">All Assignments</option>
                    {assignments
                      .filter(assignment => selectedModule === 'all' || assignment.moduleId === selectedModule)
                      .map(assignment => (
                      <option key={assignment.id} value={assignment.id}>
                        {`${assignment.sourceType === 'submission_request'
                          ? '[Submission Request] '
                          : assignment.sourceType === 'fillable_template'
                            ? '[Fillable Template] '
                            : '[Assignment] '}${assignment.title}`}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Filter by Learner</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedLearnerId}
                    onChange={(e) => setSelectedLearnerId(e.target.value)}
                  >
                    <option value="all">All Learners</option>
                    {learners.map((learner) => (
                      <option key={learner.id} value={learner.id}>
                        {learner.displayName || learner.email}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Filter by Cohort</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedCohortId}
                    onChange={(e) => setSelectedCohortId(e.target.value)}
                  >
                    <option value="all">All Cohorts</option>
                    {cohortOptions.map((cohortId) => (
                      <option key={cohortId} value={cohortId}>
                        {cohortId}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setFilterStatus('all');
                      setSelectedModule('all');
                      setSelectedAssignmentId('all');
                      setSelectedLearnerId('all');
                      setSelectedCohortId('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </Col>
            </Row>

            <Tabs defaultActiveKey="submissions" className="mb-0">
              <Tab eventKey="submissions" title={`Submissions (${filteredSubmissions.length})`}>
                <Card className="border-top-0 rounded-top-0">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Submissions ({filteredSubmissions.length})</h5>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline-primary" onClick={exportFilteredSubmissionsCsv}>
                        Download Submissions CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={downloadAllSubmissionFiles}
                        disabled={bulkDownloadLoading}
                      >
                        {bulkDownloadLoading ? 'Preparing Zip...' : 'Download All Submitted Files'}
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Module</th>
                          <th>Assignment</th>
                          <th>Submitted</th>
                          <th>Status</th>
                          <th>Score</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubmissions.slice((submissionsPage - 1) * PAGE_SIZE, submissionsPage * PAGE_SIZE).map((submission) => (
                          <tr key={submission.id}>
                            <td>
                              <div>
                                <strong>{submission.learnerName}</strong>
                                <br />
                                <small className="text-muted">{submission.learnerEmail}</small>
                              </div>
                            </td>
                            <td>{submission.moduleName}</td>
                            <td>{submission.assignmentTitle}</td>
                            <td>
                              {submission.submittedAt ?
                                new Date(submission.submittedAt?.toDate ? submission.submittedAt.toDate() : submission.submittedAt).toLocaleDateString() :
                                'Not submitted'
                              }
                            </td>
                            <td>{getStatusBadge(submission.status)}</td>
                            <td>
                              {submission.score !== null && submission.score !== undefined ?
                                `${submission.score}/${submission.maxScore}` :
                                '-'
                              }
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                {Array.isArray(submission.files) && submission.files.length > 0 && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => openFilePreview(submission.files[0], submission)}
                                  >
                                    Preview
                                  </Button>
                                )}
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleGradeSubmission(submission)}
                                  disabled={!submission.submittedAt || submission.canGrade === false}
                                >
                                  {submission.canGrade === false ? 'View Only' : (submission.status === 'graded' ? 'Edit Grade' : 'Grade')}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {filteredSubmissions.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-muted">No submissions found matching the current filters.</p>
                      </div>
                    )}
                    {filteredSubmissions.length > PAGE_SIZE && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">Showing {Math.min(submissionsPage * PAGE_SIZE, filteredSubmissions.length)} of {filteredSubmissions.length}</small>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-secondary" disabled={submissionsPage === 1} onClick={() => setSubmissionsPage(p => p - 1)}>Previous</Button>
                          <Button size="sm" variant="outline-secondary" disabled={submissionsPage * PAGE_SIZE >= filteredSubmissions.length} onClick={() => setSubmissionsPage(p => p + 1)}>Next</Button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="overview" title={`Assignment Overview (${assignmentOverviewRows.length})`}>
                <Card className="border-top-0 rounded-top-0">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Learner Assignment Overview</h5>
                    <Button size="sm" variant="outline-primary" onClick={exportAssignmentOverviewCsv}>
                      Download Overview CSV
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Module</th>
                          <th>Assignment</th>
                          <th>Status</th>
                          <th>Date Submitted</th>
                          <th>Outstanding</th>
                          <th>Feedback</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentOverviewRows.slice((overviewPage - 1) * PAGE_SIZE, overviewPage * PAGE_SIZE).map((row, index) => (
                          <tr key={`${row.learnerEmail}_${row.assignmentTitle}_${index}`}>
                            <td>
                              <div>
                                <strong>{row.learnerName}</strong>
                                <br />
                                <small className="text-muted">{row.learnerEmail}</small>
                              </div>
                            </td>
                            <td>{row.moduleName}</td>
                            <td>{row.assignmentTitle}</td>
                            <td>
                              <Badge bg={
                                row.status === 'Approved' ? 'success' :
                                  row.status === 'Rejected' ? 'danger' :
                                    row.status === 'Revision Requested' ? 'warning' :
                                      row.status === 'Missing' ? 'secondary' :
                                        row.status === 'Late' ? 'danger' :
                                          'info'
                              }>
                                {row.status}
                              </Badge>
                            </td>
                            <td>{toDisplayDate(row.submittedAt) || '-'}</td>
                            <td>{row.outstanding}</td>
                            <td>{row.feedback || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {assignmentOverviewRows.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-muted">No learner assignment overview rows found for the current filters.</p>
                      </div>
                    )}
                    {assignmentOverviewRows.length > PAGE_SIZE && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">Showing {Math.min(overviewPage * PAGE_SIZE, assignmentOverviewRows.length)} of {assignmentOverviewRows.length}</small>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-secondary" disabled={overviewPage === 1} onClick={() => setOverviewPage(p => p - 1)}>Previous</Button>
                          <Button size="sm" variant="outline-secondary" disabled={overviewPage * PAGE_SIZE >= assignmentOverviewRows.length} onClick={() => setOverviewPage(p => p + 1)}>Next</Button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </>
        )}

        {/* Grading Modal */}
        <Modal show={showGradingModal} onHide={() => setShowGradingModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Grade Assignment: {selectedAssignment?.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedSubmission && selectedAssignment && (
              <div>
                <div className="mb-3">
                  <strong>Student:</strong> {selectedSubmission.learnerName}<br />
                  <strong>Submitted:</strong> {selectedSubmission.submittedAt ?
                    new Date(selectedSubmission.submittedAt?.toDate ? selectedSubmission.submittedAt.toDate() : selectedSubmission.submittedAt).toLocaleString() :
                    'Not submitted'
                  }
                </div>

                {/* Plagiarism Check */}
                {plagiarismLoading && (
                  <Alert variant="info" className="mb-3">
                    <Spinner animation="border" size="sm" /> Checking for plagiarism...
                  </Alert>
                )}

                {plagiarismResult && plagiarismResult.flagged && (
                  <Alert variant="warning" className="mb-3">
                    <Alert.Heading>⚠️ Plagiarism Warning</Alert.Heading>
                    <p>
                      This submission has <strong>{plagiarismResult.maxSimilarityPercentage}%</strong> similarity
                      with {plagiarismResult.matches.length} other submission(s).
                    </p>
                    <details>
                      <summary className="cursor-pointer text-primary">View Similar Submissions</summary>
                      <ul className="mt-2">
                        {plagiarismResult.matches.map((match, idx) => (
                          <li key={idx}>
                            {match.userName}: {(match.score * 100).toFixed(1)}% similar
                            (Dice: {(match.diceScore * 100).toFixed(1)}%,
                            Cosine: {(match.cosineScore * 100).toFixed(1)}%)
                          </li>
                        ))}
                      </ul>
                    </details>
                  </Alert>
                )}

                {plagiarismResult && !plagiarismResult.flagged && plagiarismResult.totalCompared > 0 && (
                  <Alert variant="success" className="mb-3">
                    <small>
                      ✓ Plagiarism check passed. Compared with {plagiarismResult.totalCompared} other submission(s).
                      Max similarity: {plagiarismResult.maxSimilarityPercentage}%
                    </small>
                  </Alert>
                )}

                {/* Files submitted */}
                {selectedSubmission.files && selectedSubmission.files.length > 0 && (
                  <div className="mb-3">
                    <strong>Submitted Files:</strong>
                    <ul>
                      {selectedSubmission.files.map((file, index) => (
                        <li key={index}>
                          <Button
                            variant="link"
                            className="p-0 align-baseline"
                            onClick={() => openFilePreview(file, selectedSubmission)}
                          >
                            Preview {file.name}
                          </Button>
                          {isWordDocument(file.name || file.originalName) && (
                            <small className="text-muted ms-2">Word preview in LMS</small>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Rubric Grading */}
                {selectedAssignment.rubric && selectedAssignment.rubric.criteria ? (
                  <div className="mb-3">
                    <h6>Rubric-based Grading</h6>
                    {selectedAssignment.rubric.criteria.map((criterion, index) => (
                      <div key={index} className="mb-3 p-3 border rounded">
                        <div className="mb-2">
                          <strong>{criterion.name}</strong> (Max: {criterion.maxPoints} points)
                          <br />
                          <small className="text-muted">{criterion.description}</small>
                        </div>
                        <Form.Control
                          type="number"
                          min="0"
                          max={criterion.maxPoints}
                          step="0.5"
                          value={gradingData.rubricScores[criterion.name] || ''}
                          onChange={(e) => setGradingData({
                            ...gradingData,
                            rubricScores: {
                              ...gradingData.rubricScores,
                              [criterion.name]: e.target.value
                            }
                          })}
                        />
                      </div>
                    ))}
                    <div className="alert alert-info">
                      <strong>Total Score: {calculateRubricTotal()}/{selectedAssignment.maxScore}</strong>
                    </div>
                  </div>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Label>Score (out of {selectedSubmission.maxScore})</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max={selectedSubmission.maxScore}
                      step="0.5"
                      value={gradingData.score}
                      onChange={(e) => setGradingData({...gradingData, score: e.target.value})}
                    />
                  </Form.Group>
                )}

                {/* Feedback */}
                <Form.Group className="mb-3">
                  <Form.Label>Feedback</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={gradingData.feedback}
                    onChange={(e) => setGradingData({...gradingData, feedback: e.target.value})}
                    placeholder="Provide constructive feedback to help the student improve..."
                  />
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGradingModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveGrade}>
              Save Grade
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      <DocumentPreview
        show={showPreviewModal}
        onHide={() => {
          setShowPreviewModal(false);
          setPreviewFile(null);
        }}
        documentUrl={previewFile?.url || ''}
        documentName={previewFile?.name || previewFile?.originalName || 'Submission File'}
        documentType={(previewFile?.name || previewFile?.originalName || '').split('.').pop()?.toLowerCase() || ''}
        downloadName={previewFile?.downloadName || ''}
      />
    </>
  );
};

export default FacilitatorGradebook;
